<template>
  <v-card>
    <v-card-title>
      List Encounters
      <v-spacer></v-spacer>
      <!-- create encounter -->
      <btn
        label="Create Encounter"
        :icon="icons.mdiAccountMultiplePlus"
        @click="$router.push({ name: 'encounter-patient-find' })"
      ></btn>
    </v-card-title>

    <v-data-table
      :headers="headers"
      :items="paginatedEncounters"
      item-key="id"
      :expanded.sync="expanded"
      :page.sync="pagination.page"
      :items-per-page="pagination.itemsPerPage"
      hide-default-footer
      show-expand
      single-expand
      class="has-pagination"
      @page-count="pagination.pageCount = $event"
      @click:row="viewEncounter"
      @update:sort-by="onSortChange($event, 'by')"
      @update:sort-desc="onSortChange($event, 'desc')"
    >
      <template #top>
        <v-row class="mx-4 mt-2 pb-1">
          <!-- first name -->
          <text-field
            v-model="search.first_name"
            :append-icon="icons.mdiMagnify"
            label="First Name"
            class="col-sm-4 px-1"
          ></text-field>
          <!-- last name -->
          <text-field
            v-model="search.last_name"
            :append-icon="icons.mdiMagnify"
            label="Last Name"
            class="col-sm-4 px-1"
          ></text-field>
          <!-- date of birth -->
          <date-input
            v-model="search.dob_us"
            :append-icon="icons.mdiMagnify"
            label="Date of Birth"
            class="col-sm-4 px-1"
            rules="dob"
          ></date-input>
        </v-row>

        <v-row class="mx-4 mt-0 pb-1">
          <!-- facility filter -->
          <select-box
            v-model="search.facility"
            :items="facilities"
            label="Facility"
            class="col-sm-6 px-1"
          ></select-box>
          <!-- gender filter -->
          <select-box
            v-model="search.gender"
            :items="genders"
            label="Gender"
            class="col-sm-3 px-1"
          ></select-box>
          <!-- last 4 of ssn -->
          <text-field
            v-model="search.ssn_last_4"
            :append-icon="icons.mdiMagnify"
            label="SSN"
            mask="####"
            placeholder="####"
            class="col-sm-3 px-1"
          ></text-field>
        </v-row>

        <v-row class="mx-4 mt-0 pb-1">
          <!-- visit date last 14 days -->
          <checkbox
            v-model="search.last_fourteen_days"
            class="col-sm-1p25 px-0 pl-5"
            label="Last 14"
            hide-details
            stack
            @click="if (search.last_fourteen_days) search.visit_date = null"
          ></checkbox>
          <!-- visit date -->
          <date-picker
            v-model="search.visit_date"
            label="Visit Date"
            :max="dos_default_max"
            class="col-sm-3p25 px-1"
            clearable
            @input="if (search.visit_date) search.last_fourteen_days = false"
          ></date-picker>
          <!-- visit type filter -->
          <select-box
            v-model="search.visit_type"
            :items="visitTypes"
            label="Type of Visit"
            class="col-sm-3 px-1"
          ></select-box>
          <!-- signed? -->
          <select-box
            v-model="search.is_signed"
            :items="query"
            label="Signed"
            class="col-sm-2p25 px-1"
          ></select-box>
          <!-- synced? -->
          <select-box
            v-model="search.is_synced"
            :items="query"
            label="Synced"
            class="col-sm-2p25 px-1"
          ></select-box>
        </v-row>

        <v-row
          v-if="encountersLength >= 1000"
          class="justify-center mx-4 my-0 py-0"
        >
          <v-col class="col-sm-12">
            <alert>
              {{ filterMessage }}
            </alert>
          </v-col>
        </v-row>

        <!-- Est Pay Toggle -->
        <v-row class="justify-end mx-1 my-0 py-0">
          <v-col
            v-if="!$scribeProvider.id"
            class="d-flex justify-end" cols="auto"
          >
            <v-switch
              v-model="showEstPay"
              label="Show Est. Pay"
              hide-details
            />
            <help-icon
              class="ml-2"
              no-pos
              title="Estimated Payment"
              body="<p>To recalculate payments, please sync from the homepage.</p>"
            />
          </v-col>
          <v-col class="pr-4">
            <v-card-actions class="pa-0">
              <v-spacer></v-spacer>
              <!-- clear search/filters -->
              <btn
                label="Reset Search/Filters"
                color="secondary"
                :icon="icons.mdiUndoVariant"
                @click="resetSearchFilters"
              ></btn>
            </v-card-actions>
          </v-col>
        </v-row>

        <!-- icon help -->
        <v-expand-transition>
          <v-row
            v-show="showHelp"
            class="help-icons"
          >
            <div class="secondary--text">
              Icon Help:
            </div>
            <icon-value
              :icon="icons.mdiChevronDown"
              color="secondary"
            >
              Expand
            </icon-value>
            <icon-value
              :icon="icons.mdiDecagramOutline"
              color="secondary"
            >
              Validated
            </icon-value>
            <icon-value
              :icon="icons.mdiClipboardOutline"
              color="secondary"
            >
              Signed
            </icon-value>
            <icon-value
              :icon="icons.mdiCloudOutline"
              color="secondary"
            >
              Synced
            </icon-value>
            <icon-value
              :icon="icons.mdiDotsVertical"
              color="secondary"
            >
              Action Menu
            </icon-value>
            <v-icon @click="showHelp = !showHelp">
              {{ icons.mdiClose }}
            </v-icon>
          </v-row>
        </v-expand-transition>
      </template>

      <!-- header overrides -->
      <template #header.is_validated>
        <v-icon>{{ icons.mdiDecagramOutline }}</v-icon>
      </template>
      <template #header.is_signed>
        <v-icon>{{ icons.mdiClipboardOutline }}</v-icon>
      </template>
      <template #header.is_synced>
        <v-icon>{{ icons.mdiCloudOutline }}</v-icon>
      </template>
      <template #header.actions>
        <v-icon
          color="warning"
          @click="showHelp = !showHelp"
        >
          {{ icons.mdiInformationOutline }}
        </v-icon>
      </template>

      <!-- visit date -->
      <template #item.visit_date="{ item }">
        {{ $date(item.visit_date).format('MM/DD/YYYY') }}
      </template>

      <!-- first name -->
      <template #item.first_name="{ item }">
        <div class="table-no-wrap">
          {{ item.first_name }}
        </div>
      </template>

      <!-- last name -->
      <template #item.last_name="{ item }">
        <div class="table-no-wrap">
          {{ item.last_name }}
        </div>
      </template>

      <!-- facility -->
      <template #item.place_of_service_id="{ item }">
        <div class="table-no-wrap">
          {{ getFacilityName(item.place_of_service_id) }}
        </div>
      </template>

      <!-- Est. Pay -->
      <template #item.estimated_payment="{ item }">
        <span v-if="!showEstPay">••••</span>
        <span v-else>
          ${{ (Number(item.estimated_payment || 0) + Number(item.pay_total_amnio || 0)).toFixed(2) }}
        </span>
      </template>

      <!-- visit type -->
      <template #item.visit_type="{ item }">
        <span
          v-if="item.visit_type"
          class="visit-type"
          :class="item.visit_type.toLowerCase()"
        >
          {{ item.visit_type === 'Follow-up' ? 'F/U' : item.visit_type }}
        </span>
      </template>

      <!-- Validated -->
      <template #item.is_validated="{ item }">
        <v-icon
          :color="item.is_validated || item.is_signed ? 'success' : 'error'"
        >
          {{ item.is_validated || item.is_signed ? icons.mdiCheckDecagram : icons.mdiCloseCircle }}
        </v-icon>
      </template>

      <!-- Signed -->
      <template #item.is_signed="{ item }">
        <v-icon
          :color="item.is_signed ? 'success' : 'error'"
        >
          {{ item.is_signed ? icons.mdiClipboardCheck : icons.mdiCloseCircle }}
        </v-icon>
      </template>

      <!-- Synced -->
      <template #item.is_synced="{ item }">
        <v-icon
          :color="item.is_synced ? 'success' : 'error'"
        >
          {{ item.is_synced ? icons.mdiCloudCheckVariant : icons.mdiCloseCircle }}
        </v-icon>
      </template>

      <!-- actions -->
      <template #item.actions="{item}">
        <v-menu
          transition="slide-y-transition"
          offset-y
          left
        >
          <template #activator="{ on, attrs }">
            <v-btn
              icon
              v-bind="attrs"
              v-on="on"
            >
              <v-icon>{{ attrs['aria-expanded'] === 'true' ? icons.mdiClose : icons.mdiDotsVertical }}</v-icon>
            </v-btn>
          </template>
          <v-list>
            <v-list-item @click="viewEncounter(item)">
              <icon-value :icon="icons.mdiAccountEye">
                View/Edit Encounter
              </icon-value>
            </v-list-item>

            <v-divider></v-divider>

            <v-list-item
              :disabled="item.is_deceased"
              @click="createEncounter(item)"
            >
              <icon-value
                :disabled="item.is_deceased"
                :icon="icons.mdiAccountMultiplePlus"
              >
                Create New Encounter
              </icon-value>
            </v-list-item>

            <v-list-item @click="editPatient(item)">
              <icon-value :icon="icons.mdiAccountEdit">
                Edit This Patient
              </icon-value>
            </v-list-item>
          </v-list>
        </v-menu>
      </template>

      <!-- expanded data -->
      <template #expanded-item="{ item }">
        <!--
          The getPatient() method can be CPU intense, and was being accessed many times
          in the section below. So this for "loop" is a trick to only run the getPatient()
          once per encounter. There's only one item in the array, so it only renders once,
          but it allows the use of the "patient" value to be used without overhead.
        -->
        <td
          v-for="(patient) in [getPatient(item.patient_id)]"
          :key="patient.id"
          :colspan="headers.length"
        >
          <div>
            <label>Practice Type:</label>
            {{ $store.getters['baseData/practiceTypeFromId'](item.practice_type_id).title }}
          </div>
          <div>
            <label>Facility:</label>
            {{ getFacilityName(item.place_of_service_id) }}
          </div>
          <div>
            <label>Patient Name:</label>
            <patient-name :patient="patient"></patient-name>
          </div>
          <div>
            <label>Gender:</label>
            {{ patient.gender }}
          </div>
          <div
            v-if="typeof patient.date_deceased !== 'undefined' && patient.is_deceased"
          >
            <label>Deceased Date:</label>
            {{ $date(patient.date_deceased).format('MM/DD/YYYY') }}
          </div>
          <div v-else>
            <label>Date of Birth:</label>
            {{ $date(patient.dob).format('MM/DD/YYYY') }} ({{ $custom.getAgeDescription(patient.dob) }})
          </div>
          <div>
            <label>SSN:</label>
            {{ patient.ssn_last_4 }}
          </div>
          <div>
            <label>Fax Date:</label>
            {{ item.fax_date ? $date(item.fax_date).format('MM/DD/YYYY') : '' }}
          </div>
          <div v-if="item.is_quick_entry">
            <label>Quick Entry:</label>
            Yes
          </div>
          <div v-if="item.is_deferred_care">
            <label>Deferred Care:</label>
            Yes
          </div>
          <div>
            <label>Encounter ID:</label>
            {{ item.id }}
          </div>
        </td>
      </template>
      <template v-slot:no-data>
        <v-progress-circular
          v-if="loading"
          indeterminate
          color="primary"
          class="mx-auto my-5"
        ></v-progress-circular>
        <span v-else>No data available</span>
      </template>
    </v-data-table>

    <!-- pagination -->
    <pagination
      :page.sync="page"
      :items-per-page.sync="pagination.itemsPerPage"
      :page-count="pageCount"
    ></pagination>

    <v-card-actions></v-card-actions>
  </v-card>
</template>

<script>
/* eslint-disable no-param-reassign */

import '@/components/patients/primitives'
import { createFiltersHandler } from '@/utils/handleSearchFilters'
import {
  mdiAccountEdit,
  mdiAccountEye,
  mdiAccountMultiplePlus,
  mdiAlertDecagram,
  mdiCheckDecagram,
  mdiChevronDown,
  mdiClipboardCheck,
  mdiClipboardOutline,
  mdiClose,
  mdiCloseCircle, mdiCloudCheckVariant,
  mdiCloudOutline,
  mdiDecagram,
  mdiDecagramOutline,
  mdiDotsVertical,
  mdiFilePlus,
  mdiInformationOutline,
  mdiMagnify,
  mdiSync,
  mdiUndoVariant,
} from '@mdi/js'
import { mapGetters } from 'vuex'

const initialSearch = {
  last_fourteen_days: false,
  visit_date: null,
  visit_date_us: null,
  facility: null,
  visit_type: null,
  is_signed: null,
  is_synced: null,
  ssn_last_4: null,
  first_name: null,
  last_name: null,
  gender: null,
  dob: null,
  dob_us: null,
  patients_ids: [],
}

const initialState = {
  by: 'visit_date',
  desc: true,
}

const filtersHandler = createFiltersHandler('encountersListFilters', initialSearch)

export default {
  data() {
    return {
      dos_default_max: this.$date().format('YYYY-MM-DD'),
      search: { ...initialSearch },
      sort: { ...initialState },
      userId: this.$scribeProvider.id ?? this.$authUser.userId(),
      isFilterResetOrEmpty: true,
      showEstPay: false,
      icons: {
        mdiAccountMultiplePlus,
        mdiClipboardCheck,
        mdiCloseCircle,
        mdiCloudCheckVariant,
        mdiMagnify,
        mdiFilePlus,
        mdiUndoVariant,
        mdiSync,
        mdiCheckDecagram,
        mdiAlertDecagram,
        mdiDecagram,
        mdiDotsVertical,
        mdiClose,
        mdiAccountEdit,
        mdiAccountEye,
        mdiDecagramOutline,
        mdiClipboardOutline,
        mdiCloudOutline,
        mdiInformationOutline,
        mdiChevronDown,
      },
      showHelp: false,
      headers: [
        {
          text: 'First',
          value: 'first_name',
          align: 'name',
        },
        {
          text: 'Last',
          value: 'last_name',
          align: 'name',
        },
        {
          text: 'Visit Date',
          value: 'visit_date',
        },
        {
          text: 'Facility',
          value: 'place_of_service_id',
          align: 'facility',
        },
        {
          text: 'Est. Pay',
          value: 'estimated_payment',
          align: 'end',
        },
        {
          text: 'Type',
          value: 'visit_type',
          align: 'xsmall',
        },
        {
          text: 'Validated',
          value: 'is_validated',
          align: 'xsmall',
        },
        {
          text: 'Signed',
          value: 'is_signed',
          align: 'xsmall',
        },
        {
          text: 'Synced',
          value: 'is_synced',
          align: 'xsmall',
        },
        {
          value: 'dob',
          align: 'hide',
        },
        {
          value: 'gender',
          align: 'hide',
        },
        {
          value: 'ssn_last_4',
          align: 'hide',
        },
        {
          text: '', value: 'actions', align: 'xsmall', sortable: false,
        },
      ],
      expanded: [],
      pagination: {
        page: 1,
        pageCount: 0,
        itemsPerPage: 10,
      },
      debounceTimer: null,
      encountersLength: 0,
      filteredEncounters: [],
      loading: false,
      _restoringFromUpdateQuery: false,
    }
  },
  beforeDestroy() {
    // Clear debounceTimer to prevent memory leaks
    clearTimeout(this.debounceTimer)
    this.debounceTimer = null
    this.filteredEncounters = []
  },
  computed: {
    ...mapGetters('baseData', ['query']),
    ...mapGetters('auth', ['facilities']),
    ...mapGetters('encounters', ['isSynced', 'genders', 'visitTypes']),
    sortedEncounters() {
      return this.$custom.sortItems(this.filteredEncounters.slice(), this.sort.by, this.sort.desc)
    },
    paginatedEncounters() {
      const start = (this.pagination.page - 1) * this.pagination.itemsPerPage
      const end = start + this.pagination.itemsPerPage

      return this.sortedEncounters.slice(start, end)
    },
    filterMessage() {
      return this.isFilterResetOrEmpty
        ? 'There are too many records to display. Please use the filters or search to narrow down the results.'
        : 'Your current filter returns too many records to display. Please narrow your search by selecting a Facility and Visit Date.'
    },
    pageCount() {
      return Math.ceil(this.filteredEncounters.length / this.pagination.itemsPerPage) || 1
    },
    page: {
      get() {
        const maxPage = Math.ceil(this.filteredEncounters.length / this.pagination.itemsPerPage) || 1

        if (this.pagination.page > maxPage) {
          this.$nextTick(() => {
            this.pagination.page = 1
          })
          return 1
        }

        return this.pagination.page
      },
      set(newValue) {
        this.pagination.page = newValue
      }
    },
    // ...mapState('encounters', { encounters: 'items' }),
    // ...mapState('patients', { patients: 'items' }),
  },
  watch: {
    filteredEncounters: {
      handler(newVal) {
        this.$forceUpdate()
        if (Array.isArray(newVal) && newVal) {
          const newPageCount = Math.ceil(newVal.length / this.pagination.itemsPerPage) || 1
          this.pagination.pageCount = newPageCount

          if (this.pagination.page > newPageCount) {
            this.pagination.page = newPageCount
          }
        }
      },
      deep: true,
      immediate: true
    },
    'pagination.itemsPerPage': {
        handler(newVal) {
          const newPageCount = Math.ceil(this.filteredEncounters.length / newVal) || 1
          this.pagination.pageCount = newPageCount

          if (this.pagination.page > newPageCount) {
            this.pagination.page = 1
          }
        }
      },
    'search.visit_date': {
      handler() {
        this.search.visit_date_us = this.search.visit_date !== null ? this.$date(this.search.visit_date).format('MM/DD/YYYY') : null
      },
    },
    'search.dob_us': {
      handler() {
        this.search.dob = this.search.dob_us ? this.$date(this.search.dob_us).format('YYYY-MM-DD') : null
      },
    },
    search: {
      deep: true,
      handler() {
        if (!this._restoringFromUpdateQuery) this.handleSearchChange()
      },
    },
    pagination: {
      deep: true,
      handler() {
        this.updateQuery()
      },
    },
    sort: {
      deep: true,
      handler() {
        this.updateQuery()
      },
    },
  },
  async mounted() {
    this.$store.dispatch('loading', false)

    this.$nextTick().then(async () => {
      this._restoringFromUpdateQuery = true
      const q = this.$route.query

      // Check if we have query parameters from URL navigation
      const hasQueryParams = Object.keys(q).length > 0

      if (hasQueryParams) {
        // Restore from URL query parameters (takes precedence over localStorage)
        const { page, itemsPerPage, pageCount, ...filteredQ } = q
        this.search = {
          ...initialSearch,
          ...filteredQ ,
          facility: Number(q.facility),
          is_signed: this.$custom.strToBoolNull(q.is_signed),
          is_synced: this.$custom.strToBoolNull(q.is_synced),
          last_fourteen_days: this.$custom.strToBoolNull(q.last_fourteen_days),
        }

        this.sort.by = q.by || this.sort.by
        this.sort.desc = this.$custom.strToBoolNull(q.desc || initialState.desc)

        if (q.itemsPerPage) {
          this.pagination.itemsPerPage = Number(q.itemsPerPage)
        }
      } else {
        // Try to restore from localStorage if no URL query parameters
        const savedFilters = filtersHandler.loadFilters()

        if (savedFilters) {
          this.search = { ...savedFilters.search }
        } else {
          // No saved filters, use defaults
          this.search = { ...initialSearch }
        }

        // Always use default values for non-filter UI state
        this.sort = { ...initialState }
      }

      // Once the search object is loaded, we load the encounters and then set pagination
      // So the user get's navigated to the correct page and filters apply to the whole list.

      const { desc, by, ...filteredSearch } = this.search
      this.isFilterResetOrEmpty = filtersHandler.areFiltersEmpty(filteredSearch)

      await this.loadFilteredEncounters()
      await this.$nextTick()
      this.onSortChange(this.sort.by, this.sort.desc)
      // Calculate pageCount after encounters are ready
      this.pagination.pageCount = Math.ceil(this.filteredEncounters.length / this.pagination.itemsPerPage) || 1

      // Sets page with validation to not exceed the minimum
      if (hasQueryParams && q.page) {
        const requestedPage = Number(q.page)
        this.pagination.page = Math.min(requestedPage, this.pagination.pageCount)
      }

      this._restoringFromUpdateQuery = false
    })
  },
  methods: {
    onSortChange(value, type) {
      if (type === 'by') {
        this.sort = { ...this.sort, by: value }
      } else if (type === 'desc') {
        this.sort = { ...this.sort, desc: value }
      }
    },
    handleSearchChange() {
      clearTimeout(this.debounceTimer)
      this.debounceTimer = setTimeout(() => {
        this.isFilterResetOrEmpty = filtersHandler.areFiltersEmpty(this.search)
        this.loadFilteredEncounters()
        this.updateQuery()
        filtersHandler.saveFilters(this.search)
      }, 250)
    },
    async loadFilteredEncounters() {
      this.loading = true
      try {
        // Load default encounters if filters are empty
        this.encountersLength = 0
        this.filteredEncounters = []

        if (this.isFilterResetOrEmpty) {
          const oneWeekAgo = new Date()
          oneWeekAgo.setDate(oneWeekAgo.getDate() - 7)

          const defaultEncounters = this.$store.state.encounters.items.filter(
            x => x.created_by_user_id === this.userId
              && x.deleted !== true
              && (!x.is_signed || (x.is_signed && new Date(x.signed_time) >= oneWeekAgo)),
          )

          this.encountersLength = defaultEncounters.length

          if (this.encountersLength >= 1000) {
            this.filteredEncounters = []
            return
          }

          const batchSize = 200
          for (let i = 0; i < defaultEncounters.length; i += batchSize) {
            const batch = defaultEncounters.slice(i, i + batchSize)
            batch.forEach(encounter => {
              const patient = this.getPatient(encounter.patient_id)
              if (patient) {
                Object.assign(encounter, {
                  first_name: patient.first_name,
                  last_name: patient.last_name,
                  gender: patient.gender,
                  ssn_last_4: patient.ssn_last_4,
                  dob: patient.dob,
                  is_deceased: patient.is_deceased,
                })
              }
            })
            await new Promise(resolve => setTimeout(resolve, 0))
          }

          defaultEncounters.sort((a, b) => new Date(b.created) - new Date(a.created))
          this.filteredEncounters = [...defaultEncounters]
          return
        }

        let relatedPatientsIds = []

        // Check if any of patient filters is empty
        if (this.search.gender || this.search.dob || this.search.first_name || this.search.last_name || this.search.ssn_last_4) {
          const matchedIds = this.$store.getters['patients/getPatientsIdsBySearch'](this.search)
          relatedPatientsIds = matchedIds.length ? matchedIds : ['none']
        }

        // Filter by search
        const filteredEncounters = this.$store.getters['encounters/filteredEncountersBySearch']({ ...this.search,
          user_id: this.userId,
          patients_ids: relatedPatientsIds
        })

        this.encountersLength = filteredEncounters.length

        if (this.encountersLength >= 1000 && !this.isFilterResetOrEmpty) {
          this.filteredEncounters = []
          return
        }

        if (this.encountersLength <= 1000) {
          this.filteredEncounters = []
          const batchSize = 200
          let results = []

          for (let i = 0; i < filteredEncounters.length; i += batchSize) {
            const batch = filteredEncounters.slice(i, i + batchSize)
            batch.forEach(encounter => {
              const patient = this.getPatient(encounter.patient_id)
              if (patient) {
                Object.assign(encounter, {
                  first_name: patient.first_name,
                  last_name: patient.last_name,
                  gender: patient.gender,
                  ssn_last_4: patient.ssn_last_4,
                  dob: patient.dob,
                  is_deceased: patient.is_deceased,
                })
              }
            })

            results = results.concat(batch)
            await new Promise(resolve => setTimeout(resolve, 0))
          }
          filteredEncounters.sort((a, b) => new Date(b.created) - new Date(a.created))
          this.filteredEncounters = [...results]
        }
      } finally {
        this.loading = false
      }
    },
    updateQuery() {
      // Replace the query
      const query = { ...this.search, ...this.sort, ...this.pagination }
      Object.keys(query).forEach(key => { if (query[key] === null || typeof query[key] === 'undefined') query[key] = '' })
      window.history.replaceState({}, null, `${this.$route.path}?${new URLSearchParams(query).toString()}`)
      this.$store.commit('route/updateQuery', query)
      this.expanded = []
    },
    resetSearchFilters() {
      this.search = { ...initialSearch }
      this.sort = { ...initialState }
      this.pagination.page = 1
      this.showHelp = false
      this.isFilterResetOrEmpty = true
      filtersHandler.clearFilters()
    },
    createEncounter(item) {
      // Load the complete data for the related patient and encounters.
      this.$store.dispatch('patients/loadPatientData', item.patient_id)
      this.$store.dispatch('encounters/loadEncounterData', item.patient_id)

      this.$router.push({ name: 'create-encounter', query: { id: item.patient_id }, params: { practiceTypeId: item.practice_type_id } })
    },
    editPatient(item) {
      // Load the complete data for the related patient and encounters.
      this.$store.dispatch('patients/loadPatientData', item.patient_id)
      this.$store.dispatch('encounters/loadEncounterData', item.patient_id)

      this.$router.push({ name: 'edit-patient', query: { id: item.patient_id } })
    },
    async viewEncounter(item) {
      // Fully rehydrate the patient and encounter before navigating,
      // Prevents data loss from empty or lightweight copy
      await Promise.all([
        this.$store.dispatch('patients/loadPatientData', item.patient_id),
        this.$store.dispatch('encounters/loadEncounterData', item.patient_id),
      ])

      if ((item.is_signed && item.is_synced) || item.is_deferred_care) {
        // View signed surgical note
        this.$router.push({
          name: this.$store.getters['baseData/practiceTypeFromId'](item.practice_type_id).noteRoute,
          query: { id: item.id },
        })
      } else if (item.created_by_user_id !== this.userId) {
        // Don't allow a different provider to edit an encounter
        this.$store.dispatch('notify', { value: 'You can\'t edit this encounter as you\'re not the provider who created it.', color: 'error' })
      } else {
        // Edit encounter note
        this.$store.dispatch('loading', true)
        this.$router.push({
          name: this.$store.getters['baseData/practiceTypeFromId'](item.practice_type_id).encounterRoute,
          query: { id: item.id },
        })
      }
    },
    getPatient(patientId) {
      return this.$store.getters['patients/getById'](patientId)
    },
    getFacilityName(placeOfServiceId) {
      const facilityName = this.$store.getters['facilities/getById'](placeOfServiceId).title
      return facilityName || 'No facility found'
    },
  },
}
</script>
